import React, { forwardRef } from "react"
import {
  Box, BoxProps, Divider, Heading, Link, ListItem, OrderedList, Text, UnorderedList,
} from "@chakra-ui/react"
import { PortableText, PortableTextComponents } from "@portabletext/react"
import ZoomableImage from "./ZoomableImage"
import { UpdateBody, UpdateBodyImage } from "./types"
import { WHATS_NEW_COLORS, WHATS_NEW_IMAGE_SIZES } from "./colors"

// d.io's post body (dio-marketing src/styles/prose.css), so a post looks the same in both places.
// The Text colour mark sets --text-color, which bold and links read before their own colour.
const prose = {
  color: WHATS_NEW_COLORS.text2,
  fontSize: "16px",
  lineHeight: 1.5,
  letterSpacing: "-0.011em",
  overflowWrap: "break-word",
  textWrap: "pretty",
  "& > * + *": { mt: "20px" },
  "& > :first-child": { mt: 0 },
  "& :is(h2, h3, h4, h5)": { color: WHATS_NEW_COLORS.text1, textWrap: "balance" },
  "& > :is(h2, h3)": {
    mt: "28px", fontSize: "20px", fontWeight: "semibold", lineHeight: 1.4, letterSpacing: "-0.014em",
  },
  "& > :is(h4, h5)": {
    mt: "24px", fontSize: "16px", fontWeight: "semibold", lineHeight: 1.4, letterSpacing: "-0.008em",
  },
  "& > :is(h2, h3, h4, h5) + *": { mt: "12px" },
  "& [data-text-color]": { color: "var(--text-color)" },
  "& strong": {
    color: `var(--text-color, var(--strong-color, ${WHATS_NEW_COLORS.text1}))`,
    fontWeight: "semibold",
  },
  "& em": { fontStyle: "italic" },
  "& a": {
    "--strong-color": WHATS_NEW_COLORS.link,
    color: `var(--text-color, ${WHATS_NEW_COLORS.link})`,
    textDecorationLine: "none",
    textDecorationThickness: "1px",
    textUnderlineOffset: "3px",
    borderRadius: "2px",
  },
  "& a:hover": { textDecorationLine: "underline" },
  "& :is(ul, ol)": { pl: "24px" },
  "& > :is(ul, ol)": { mt: "19px" },
  "& ul": { listStyleType: "disc" },
  "& li ul": { listStyleType: "circle" },
  "& ol": { listStyleType: "decimal" },
  "& li::marker": { color: WHATS_NEW_COLORS.text3 },
  "& li + li, & li > :is(ul, ol)": { mt: "10px" },
  "& > figure": { mt: "16px", mb: "21px" },
  "& hr": { my: "28px", borderColor: WHATS_NEW_COLORS.line, opacity: 1 },
}

// The Studio validates these, but writes through Sanity's API skip that validation
const isSafeHref = (href?: string) => /^(https?:|mailto:)/i.test(href ?? "")
const isHexColor = (color?: string) => /^#[0-9a-f]{3,8}$/i.test(color ?? "")

// The post title is a heading too, so the body's headings go one level down, as in d.io's timeline
const components: PortableTextComponents = {
  block: {
    normal: ({ children }) => <Text>{children}</Text>,
    h2: ({ children }) => <Heading as="h3">{children}</Heading>,
    h3: ({ children }) => <Heading as="h4">{children}</Heading>,
  },
  list: {
    bullet: ({ children }) => <UnorderedList ml={0}>{children}</UnorderedList>,
    number: ({ children }) => <OrderedList ml={0}>{children}</OrderedList>,
  },
  listItem: ({ children }) => <ListItem>{children}</ListItem>,
  marks: {
    textColor: ({ value, children }) => (
      isHexColor(value?.color?.hex) ? (
        <Box as="span" data-text-color="" sx={{ "--text-color": value.color.hex }}>
          {children}
        </Box>
      ) : children
    ),
    link: ({ value, children }) => (
      isSafeHref(value?.href) ? <Link href={value.href} isExternal>{children}</Link> : children
    ),
  },
  types: {
    image: ({ value }: { value: UpdateBodyImage }) => (
      <Box as="figure">
        <ZoomableImage image={value} sizes={WHATS_NEW_IMAGE_SIZES} />
      </Box>
    ),
    divider: () => <Divider />,
  },
}

type Props = {
  body: UpdateBody
} & BoxProps

const WhatsNewPostBody = forwardRef<HTMLDivElement, Props>(({ body, ...props }, ref) => (
  <Box ref={ref} sx={prose} {...props}>
    <PortableText value={body} components={components} />
  </Box>
))

export default WhatsNewPostBody
